// Typed error hierarchy returned by Client methods. Callers branch on
// instanceof: `if (err instanceof TOTPRequiredError) { showPrompt() }`.
//
// The API replies with RFC 7807 problem+json bodies. ApiError preserves
// the parsed body verbatim so callers can surface server-provided
// detail strings (already localised by the backend when relevant).

/** RFC 7807 problem body shape — narrow on the fields we actually read. */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  [extension: string]: unknown;
}

/** Base class — every SDK rejection that came from an HTTP response. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;

  constructor(status: number, problem: ProblemDetails, message?: string) {
    super(message ?? problem.detail ?? problem.title ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
  }
}

/** 401 — generic auth failure. */
export class UnauthorizedError extends ApiError {
  constructor(problem: ProblemDetails) {
    super(401, problem);
    this.name = "UnauthorizedError";
  }
}

/**
 * 401 with `detail: "totp_code required"` — the user has TOTP enrolled
 * but the request did not include a code. Render the second-factor
 * prompt rather than "wrong password". Distinct from UnauthorizedError
 * so callers can branch confidently.
 */
export class TOTPRequiredError extends ApiError {
  constructor(problem: ProblemDetails) {
    super(401, problem);
    this.name = "TOTPRequiredError";
  }
}

/**
 * 409 — typically `email maps to multiple schools` (T-8.1). The client
 * should retry the login with a school_slug, usually after showing a
 * school picker.
 */
export class ConflictError extends ApiError {
  constructor(problem: ProblemDetails) {
    super(409, problem);
    this.name = "ConflictError";
  }
}

/** 429 — token-bucket rate limit exceeded. retryAfter from Retry-After. */
export class RateLimitError extends ApiError {
  readonly retryAfterSeconds: number | undefined;

  constructor(problem: ProblemDetails, retryAfterSeconds: number | undefined) {
    super(429, problem);
    this.name = "RateLimitError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** 400 — payload was rejected. The problem.detail names the bad field. */
export class ValidationError extends ApiError {
  constructor(problem: ProblemDetails) {
    super(400, problem);
    this.name = "ValidationError";
  }
}

/**
 * isApiError narrows unknown → ApiError. Easier to read at call sites
 * than the bare `instanceof` chain and friendlier to bundlers that
 * tree-shake class identity.
 */
export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}
